import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Segment, Header, Button, Icon, Message, Table, Label, Confirm } from 'semantic-ui-react';
import GlassPageWrapper from '../../../components/Layout/GlassPageWrapper';
import { quizService, QuizListDto } from '../../../services/learning/QuizService';
import { useAuth } from '../../../context/AuthContext';
import ManageQuizAttemptsModal from '../../../components/Quiz/ManageQuizAttemptsModal';

const QuizManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [quizzes, setQuizzes] = useState<QuizListDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [quizToDelete, setQuizToDelete] = useState<string | null>(null);
  const [manageAttemptsModalOpen, setManageAttemptsModalOpen] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState<{ id: string; title: string } | null>(null);

  useEffect(() => {
    if (!hasRole('Admin')) {
      navigate('/learning');
      return;
    }

    loadQuizzes();
  }, [hasRole, navigate]);

  const loadQuizzes = async () => {
    try {
      setLoading(true);
      const data = await quizService.getQuizzes();
      setQuizzes(data);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar questionários');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!quizToDelete) return;

    try {
      await quizService.deleteQuiz(quizToDelete);
      await loadQuizzes();
      setDeleteConfirmOpen(false);
      setQuizToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Erro ao deletar questionário');
    }
  };

  const handleOpenManageAttempts = (quiz: QuizListDto) => {
    setSelectedQuiz({ id: quiz.id, title: quiz.title });
    setManageAttemptsModalOpen(true);
  };

  const handleCloseManageAttempts = () => {
    setManageAttemptsModalOpen(false);
    setSelectedQuiz(null);
  };

  if (!hasRole('Admin')) {
    return null;
  }

  return (
    <GlassPageWrapper title="Gestão de Questionários" icon="edit">
      <Segment>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <Header as="h3">Lista de Questionários</Header>
          <Button primary onClick={() => navigate('/learning/quizzes/new')}>
            <Icon name="plus" />
            Novo Questionário
          </Button>
        </div>

        {error && (
          <Message negative>
            <Message.Header>Erro</Message.Header>
            <p>{error}</p>
          </Message>
        )}

        {loading ? (
          <Message info>
            <Icon name="spinner" loading />
            Carregando questionários...
          </Message>
        ) : quizzes.length === 0 ? (
          <Message info>
            <Message.Header>Nenhum questionário encontrado</Message.Header>
            <p>Crie seu primeiro questionário clicando no botão acima.</p>
          </Message>
        ) : (
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Título</Table.HeaderCell>
                <Table.HeaderCell>Categoria</Table.HeaderCell>
                <Table.HeaderCell>Questões</Table.HeaderCell>
                <Table.HeaderCell>Pontuação Mínima</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Criado por</Table.HeaderCell>
                <Table.HeaderCell>Ações</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {quizzes.map(quiz => (
                <Table.Row key={quiz.id}>
                  <Table.Cell>{quiz.title}</Table.Cell>
                  <Table.Cell>
                    {quiz.category && (
                      <Label color="blue" size="small">
                        {quiz.category}
                      </Label>
                    )}
                  </Table.Cell>
                  <Table.Cell>{quiz.questionsCount}</Table.Cell>
                  <Table.Cell>{quiz.minimumScore}%</Table.Cell>
                  <Table.Cell>
                    {quiz.isActive ? (
                      <Label color="green" size="small">Ativo</Label>
                    ) : (
                      <Label color="grey" size="small">Inativo</Label>
                    )}
                  </Table.Cell>
                  <Table.Cell>{quiz.creatorName}</Table.Cell>
                  <Table.Cell>
                    <Button
                      size="small"
                      icon="clipboard check"
                      color="blue"
                      title="Gerenciar Tentativas"
                      onClick={() => handleOpenManageAttempts(quiz)}
                    />
                    <Button
                      size="small"
                      icon="edit"
                      onClick={() => navigate(`/learning/quizzes/${quiz.id}/edit`)}
                    />
                    <Button
                      size="small"
                      icon="trash"
                      color="red"
                      onClick={() => {
                        setQuizToDelete(quiz.id);
                        setDeleteConfirmOpen(true);
                      }}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Segment>

      {selectedQuiz && (
        <ManageQuizAttemptsModal
          open={manageAttemptsModalOpen}
          onClose={handleCloseManageAttempts}
          quizId={selectedQuiz.id}
          quizTitle={selectedQuiz.title}
        />
      )}

      <Confirm
        open={deleteConfirmOpen}
        onCancel={() => {
          setDeleteConfirmOpen(false);
          setQuizToDelete(null);
        }}
        onConfirm={handleDelete}
        content="Tem certeza que deseja deletar este questionário?"
        confirmButton="Deletar"
        cancelButton="Cancelar"
      />
    </GlassPageWrapper>
  );
};

export default QuizManagementPage;

